import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type {
  ResolvedOpeningFraming,
  ResolvedOpeningFramingTable,
  WallOpeningFramingConfigGroup,
  WallOpeningFramingResolver,
} from "building-types/framing/wallFramingTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { WallGap } from "building-types/walls/wallGap";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { defaultLumberComponentId } from "doc/elements/framing/defaultLumberComponents";
import { resolveRoughOpeningFramingSettings } from "../elements/walls/wallFramingUtils";
import {
  buildWallOpeningFramingConfigGroup,
  pushUnresolvedWallOpeningHeaderIssue,
  validateWallOpeningFramingConfigGroup,
} from "./wallOpeningFramingConfigTools";

type OpeningPlacement = GeneratedWindowPlacement | GeneratedDoorPlacement;

interface OpeningPlacementEntry {
  readonly placement: OpeningPlacement;
  readonly isDoor: boolean;
}

interface ResolveOpeningFramingArgs {
  readonly openingId: string;
  readonly roughWidth: number;
  readonly isDoor: boolean;
  readonly placement: OpeningPlacement;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly headerFramingSettings: HeaderFramingSettings;
}

export function resolveOpeningFraming({
  openingId,
  roughWidth,
  isDoor,
  placement,
  targetedOpeningSettings,
  headerAssemblies,
  framingComponents,
  headerFramingSettings,
}: ResolveOpeningFramingArgs): ResolvedOpeningFraming {
  const gap: WallGap = {
    openings: [placement],
    start: 0,
    center: roughWidth / 2,
    end: roughWidth,
    bottom: 0,
    height: 0,
    hasStartCasing: false,
    hasEndCasing: false,
  };

  const resolved = resolveRoughOpeningFramingSettings({
    openings: [placement],
    gap,
    isDoor,
    targetedOpeningSettings,
    headerAssemblies,
    framingComponents,
    headerFramingSettings,
  });

  // When neither an override nor a header rule resolves an assembly, fall back to the span-sized
  // built-in dimensional component (matches legacy span-based header sizing).
  const headerComponentId =
    resolved.headerAssemblyId === undefined ? defaultLumberComponentId(resolved.headerProfile.type) : undefined;

  return {
    openingId,
    headerAssemblyId: resolved.headerAssemblyId,
    headerAssemblyName: resolved.headerAssemblyName,
    headerComponentId,
    framingDisabled: false,
    numberOfJackStuds: resolved.numberOfJackStuds,
    numberOfKingStuds: resolved.numberOfKingStuds,
    headerCount: resolved.headerCount,
    roughOpeningBorder: resolved.roughOpeningBorder,
    hasCrippleStudEnds: resolved.hasCrippleStudEnds,
    hasCrippleStudsBelowOpening: resolved.hasCrippleStudsBelowOpening,
    hasCrippleStudsAboveOpening: resolved.hasCrippleStudsAboveOpening,
    hasTopHeaderSillPlate: resolved.hasTopHeaderSillPlate,
  };
}

export function resolvedOpeningFramingToConfigGroup(
  resolved: ResolvedOpeningFraming,
  inheritedStudComponentId: ForeignEntityId<"FRAMING_COMPONENT">,
  headerAssemblies: Table<HeaderAssemblyEntity>,
): WallOpeningFramingConfigGroup {
  return buildWallOpeningFramingConfigGroup(inheritedStudComponentId, resolved, headerAssemblies);
}

interface WallOpeningFramingResolverInputs {
  readonly placementsByOpeningId: ReadonlyMap<string, OpeningPlacementEntry>;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly headerFramingSettings: HeaderFramingSettings;
}

export interface WallOpeningFramingResolverBundle {
  readonly resolveForRegion: (
    inheritedStudComponentId: ForeignEntityId<"FRAMING_COMPONENT">,
    issues: BuildingGenerationIssue[],
  ) => WallOpeningFramingResolver;
  getResolvedMap(): ResolvedOpeningFramingTable;
}

export function createWallOpeningFramingResolver(
  inputs: WallOpeningFramingResolverInputs,
): WallOpeningFramingResolverBundle {
  const { placementsByOpeningId, targetedOpeningSettings, headerAssemblies, framingComponents, headerFramingSettings } =
    inputs;

  const cache = new Map<string, ResolvedOpeningFraming>();
  const resolvedMap: RWTable<ResolvedOpeningFraming> = {};

  const resolveForRegion = (
    inheritedStudComponentId: ForeignEntityId<"FRAMING_COMPONENT">,
    issues: BuildingGenerationIssue[],
  ): WallOpeningFramingResolver => {
    return ({ openingId, roughWidth }) => {
      const entry = placementsByOpeningId.get(openingId);
      if (!entry) {
        return buildWallOpeningFramingConfigGroup(inheritedStudComponentId, undefined, headerAssemblies);
      }

      const cacheKey = `${openingId}:${roughWidth}`;
      let resolved = cache.get(cacheKey);
      if (!resolved) {
        resolved = resolveOpeningFraming({
          openingId,
          roughWidth,
          isDoor: entry.isDoor,
          placement: entry.placement,
          targetedOpeningSettings,
          headerAssemblies,
          framingComponents,
          headerFramingSettings,
        });
        cache.set(cacheKey, resolved);
      }

      resolvedMap[openingId] = resolved;

      if (resolved.headerAssemblyId === undefined && resolved.headerComponentId === undefined) {
        pushUnresolvedWallOpeningHeaderIssue(issues, openingId);
      }

      const configGroup = buildWallOpeningFramingConfigGroup(inheritedStudComponentId, resolved, headerAssemblies);
      validateWallOpeningFramingConfigGroup(configGroup, framingComponents, issues, `Opening "${openingId}" framing`);
      return configGroup;
    };
  };

  return { resolveForRegion, getResolvedMap: () => resolvedMap };
}

export function buildOpeningPlacementsByOpeningId(
  windowPlacements: Table<GeneratedWindowPlacement>,
  doorPlacements: Table<GeneratedDoorPlacement>,
): Map<string, OpeningPlacementEntry> {
  const byOpeningId = new Map<string, OpeningPlacementEntry>();

  for (const placement of Object.values(windowPlacements)) {
    const openingId = placement.handle?.id ?? placement.id;
    if (openingId) {
      byOpeningId.set(openingId, { placement, isDoor: false });
    }
  }

  for (const placement of Object.values(doorPlacements)) {
    const openingId = placement.handle?.id ?? placement.id;
    if (openingId) {
      byOpeningId.set(openingId, { placement, isDoor: true });
    }
  }

  return byOpeningId;
}
